/**
 * Aufgaben — Board, Liste und Zeitstrahl in der Form der Vorlage.
 *
 * Der Entwurf zeigt die Aufgaben als eigene Seite mit drei Ansichten: Board
 * mit dunklen Spaltenköpfen (Tafel), Liste mit Fristgruppen und Zeitstrahl
 * mit Wochenachse. Die Datenlogik (Filter, Gruppierung, Spalten) bleibt die
 * der bisherigen Liste; geändert ist die Bauform: Zeilen statt Karten,
 * Punkte statt Marken, Mono statt Serifen.
 *
 * Die Filterzeile zeigt die aktiven Filter als Mono-Text, wie der Entwurf
 * sie zeichnet. Bedient wird über den ruhigen Verweis „Filter" rechts, der
 * das Popover mit den Auswahlfeldern öffnet — die Seite bleibt dadurch
 * zeilenförmig, ohne Bedienelemente in die Zeile zu zwingen.
 */

import { useMemo, useState } from "react";
import {
  Link,
  useNavigate,
  useRouterState,
  useSearch,
} from "@tanstack/react-router";
import {
  AUFGABEN_ART_LABEL,
  AUFGABEN_GRUPPEN,
  aufgabenartSchema,
  formuliereFrist,
  gruppeVon,
  type AufgabenGruppe,
  type Rolle,
} from "@cubic/shared";
import { Button } from "@/components/Button";
import { Input } from "@/components/Input";
import { Label } from "@/components/Label";
import { markdownAuszug } from "@/lib/markdown-auszug";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/Select";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/Dialog";
import { Fehlerzeile } from "@/components/Fehlerzeile";
import { Leerzustand } from "@/components/Leerzustand";
import { Seitenkopf } from "@/components/entwurf/Seitenkopf";
import { Mikrolabel } from "@/components/entwurf/Mikrolabel";
import { Rubrik } from "@/components/entwurf/Rubrik";
import { Kennung, zeileKlassen, Zeilentitel } from "@/components/entwurf/Zeile";
import { Zustandspunkt } from "@/components/entwurf/Zustandspunkt";
import { Prioritaetspunkte } from "@/components/entwurf/Prioritaetspunkte";
import { Reiter, Reiterleiste } from "@/components/entwurf/Reiter";
import { Schalter } from "@/components/entwurf/Schalter";
import { verweisKlassen } from "@/components/entwurf/Verweis";
import { istRandlos } from "@/lib/rahmen";
import {
  fehlerMeldung,
  useAufgabeAnlegen,
  useAufgabeArchivieren,
  useAufgabenFuerProjekte,
  useIch,
  useProjekte,
  useZustaendeFuerProjekte,
} from "@/lib/haken";
import {
  filterAusSuche,
  filtereAufgaben,
  hatAktivenFilter,
  leererFilter,
  sortiereAufgaben,
  sucheAusFilter,
  type AufgabenFilter,
} from "@/lib/filter";
import { darfAnlegen, darfAktion } from "@/lib/rollen";
import {
  ZUSTAND,
  istAbgeschlossen,
  zustandAusGruppe,
} from "@/lib/entwurf-zustaende";
import { cx } from "@/lib/utils";
import { leerArt, leerText } from "@/lib/leerzustand";
import {
  ALLE_PROJEKTE,
  useProjektAuswahl,
} from "@/lib/projekt-auswahl";

/**
 * Dringlichkeit als drei Punkte, nicht als Wort.
 *
 * Der Entwurf zeigt sie als Reihe: gesetzte Punkte in Tinte, offene in der
 * Ruhefarbe. Drei ist die Obergrenze — mehr Stufen unterscheidet auf sieben
 * Pixeln niemand mehr.
 */

/** Dringlichkeit als drei Punkte, nicht als Wort. */
export function Prioritaetsstrich({ wert }: { wert: number }) {
  const stufen = [0, 1, 2] as const;
  return (
    <span
      className="inline-flex h-[26px] w-[3px] flex-col justify-end gap-px"
      aria-label={`Priorität ${wert}`}
    >
      {stufen.map((i) => (
        <span
          key={i}
          className={cx(
            "w-full flex-1 rounded-sm",
            wert > i
              ? "bg-gray-800 dark:bg-gray-200"
              : "bg-gray-200 dark:bg-gray-800",
          )}
        />
      ))}
    </span>
  );
}

const GRUPPEN_LABEL: Record<AufgabenGruppe, string> = {
  ueberfaellig: "Überfällig",
  heute: "Heute",
  diese_woche: "Diese Woche",
  spaeter: "Später",
  ohne_datum: "Ohne Datum",
};

/** Gruppen-Ton in der Listenansicht: Überfällig verlangt, Ohne Datum schweigt. */
const GRUPPEN_TON: Record<AufgabenGruppe, string> = {
  ueberfaellig: "text-akzent",
  heute: "text-tinte",
  diese_woche: "text-tinte",
  spaeter: "text-tinte",
  ohne_datum: "text-tinte4",
};

/** Punkt im dunklen Spaltenkopf des Boards — Tafel-Helligkeit je Gruppe. */
const TAFEL_PUNKT: Record<string, string> = {
  offen: "bg-[oklch(0.74_0.003_30)]",
  in_arbeit: "bg-[oklch(0.935_0.003_30)]",
  abnahme: "bg-[oklch(0.84_0.003_30)]",
  erledigt: "bg-[oklch(0.68_0.003_30)]",
  verworfen: "bg-[oklch(0.68_0.003_30)]",
};

function aufgabenartLabel(art: string): string {
  const gelesen = aufgabenartSchema.safeParse(art);
  return gelesen.success ? AUFGABEN_ART_LABEL[gelesen.data] : art || "Aufgabe";
}

/** Fenster des Zeitstrahls: acht Wochen ab Montag der aktuellen Woche. */
function wochenFenster(jetzt: Date): { start: Date; tage: number } {
  const start = new Date(jetzt);
  start.setHours(0, 0, 0, 0);
  const tag = (start.getDay() + 6) % 7; // Montag = 0
  start.setDate(start.getDate() - tag);
  return { start, tage: 56 };
}

function positionInFenster(
  wert: Date,
  fensterStart: Date,
  fensterTage: number,
): number {
  const ms = wert.getTime() - fensterStart.getTime();
  const anteil = (ms / (fensterTage * 24 * 60 * 60 * 1000)) * 100;
  return Math.min(Math.max(anteil, 0), 100);
}

export function AufgabenListe({
  festesProjektId,
  rolle,
  neuOffen: neuOffenProp,
  onNeuOffen,
  zeigeProjektFilter = true,
  mitKopf = true,
}: {
  festesProjektId?: string;
  rolle?: Rolle | null;
  neuOffen?: boolean;
  onNeuOffen?: (o: boolean) => void;
  zeigeProjektFilter?: boolean;
  mitKopf?: boolean;
}) {
  const search = useSearch({ strict: false }) as Record<
    string,
    string | undefined
  >;
  const navigate = useNavigate();
  /*
   * Als eigene Seite setzt die Aufgabenliste ihre Abstände selbst — der
   * Entwurf gibt ihr `padding: 56px 0 0` und legt die 36px nur um Titel,
   * Reiter, Filterzeile und Liste. Nur so laufen Board und Zeitstrahl über die
   * volle Breite. Als Reiter im Projektdetail steckt sie dagegen im Rahmen der
   * Hülle und darf nichts dazutun. `istRandlos` entscheidet dasselbe für die
   * Hülle, also fragen beide dieselbe Stelle.
   */
  const pfad = useRouterState({ select: (s) => s.location.pathname });
  const randlos = istRandlos(pfad);
  const seitenrand = randlos ? "px-4 sm:px-6 lg:px-9" : "";
  const ich = useIch();
  const projekte = useProjekte();
  const { auswahl } = useProjektAuswahl();
  const kontextProjektId =
    festesProjektId ?? (auswahl === ALLE_PROJEKTE ? undefined : auswahl);
  const filter = useMemo(() => {
    const f = filterAusSuche(search);
    if (kontextProjektId) {
      return { ...f, projektIds: [kontextProjektId] };
    }
    return f;
  }, [search, kontextProjektId]);

  const projektIds = useMemo(
    () => (projekte.data ?? []).map((p) => p.id),
    [projekte.data],
  );
  const datenProjektIds = kontextProjektId ? [kontextProjektId] : projektIds;
  const aufgabenQ = useAufgabenFuerProjekte(datenProjektIds, filter);
  const zustaende = useZustaendeFuerProjekte(datenProjektIds);

  const [internNeu, setzeInternNeu] = useState(false);
  const neuOffen = neuOffenProp ?? internNeu;
  const setzeNeu = onNeuOffen ?? setzeInternNeu;
  const [projektNeu, setzeProjektNeu] = useState(kontextProjektId ?? "");
  const [formFehler, setzeFormFehler] = useState<ReturnType<
    typeof fehlerMeldung
  > | null>(null);
  const [formTitel, setzeFormTitel] = useState("");
  const [ansicht, setzeAnsicht] = useState<"board" | "liste" | "zeit">(
    search["ansicht"] === "board"
      ? "board"
      : search["ansicht"] === "zeit"
        ? "zeit"
        : "liste",
  );
  const [filterOffen, setzeFilterOffen] = useState(false);

  const anlegen = useAufgabeAnlegen(filter);
  const archivMut = useAufgabeArchivieren(filter);

  const zeitzone = "Europe/Berlin";
  const jetzt = useMemo(() => new Date(), [aufgabenQ.dataUpdatedAt]);
  const zustandsGruppen = useMemo(
    () =>
      new Map(
        (zustaende.data ?? []).map((zustand) => [
          zustand.id,
          zustand.gruppe,
        ]),
      ),
    [zustaende.data],
  );

  const angereichert = useMemo(() => {
    const roh = aufgabenQ.data ?? [];
    const mitGruppe = roh.map((a) => {
      const zustandsGruppe = a.zustandId
        ? zustandsGruppen.get(a.zustandId)
        : undefined;
      const erledigt =
        zustandsGruppe === "erledigt" || zustandsGruppe === "verworfen";
      const gruppe = gruppeVon(
        { faelligAm: a.faelligAm, erledigt },
        zeitzone,
        jetzt,
      );
      return {
        ...a,
        gruppe,
        fachlichOffen: !erledigt,
        zugewiesenIds: [] as string[],
        labelIds: [] as string[],
      };
    });
    const gefiltert = filtereAufgaben(mitGruppe, filter, {
      personId: ich.data?.personId,
    });
    return sortiereAufgaben(gefiltert, filter.sortierung);
  }, [
    aufgabenQ.data,
    filter,
    ich.data?.personId,
    jetzt,
    zeitzone,
    zustandsGruppen,
  ]);

  const gruppiert = useMemo(() => {
    const m = new Map<AufgabenGruppe, typeof angereichert>();
    for (const g of AUFGABEN_GRUPPEN) m.set(g, []);
    for (const a of angereichert) {
      const g = (a.gruppe ?? "ohne_datum") as AufgabenGruppe;
      m.get(g)!.push(a);
    }
    return m;
  }, [angereichert]);

  const projekteById = useMemo(() => {
    const m = new Map<string, { kuerzel: string; name: string }>();
    for (const p of projekte.data ?? []) m.set(p.id, p);
    return m;
  }, [projekte.data]);

  const zustaendeById = useMemo(() => {
    const m = new Map<string, { name: string; gruppe: string }>();
    for (const z of zustaende.data ?? []) m.set(z.id, z);
    return m;
  }, [zustaende.data]);

  /** Board: Spalten nach Zustandsname (Paket 2 / 16). */
  const boardSpalten = useMemo(() => {
    const definitionen = new Map<
      string,
      { id: string; name: string; gruppe: string; zustandIds: Set<string> }
    >();
    for (const zustand of zustaende.data ?? []) {
      const schluessel = `${zustand.gruppe}:${zustand.name}`;
      const definition = definitionen.get(schluessel) ?? {
        id: schluessel,
        name: zustand.name,
        gruppe: zustand.gruppe,
        zustandIds: new Set<string>(),
      };
      definition.zustandIds.add(zustand.id);
      definitionen.set(schluessel, definition);
    }
    const spalten = [...definitionen.values()].map((definition) => ({
      id: definition.id,
      name: definition.name,
      gruppe: definition.gruppe,
      aufgaben: angereichert.filter(
        (aufgabe) =>
          Boolean(aufgabe.zustandId) &&
          definition.zustandIds.has(aufgabe.zustandId!),
      ),
    }));
    const ohne = angereichert.filter((a) => !a.zustandId);
    if (ohne.length > 0) {
      spalten.push({
        id: "__ohne__",
        name: "Ohne Zustand",
        gruppe: "offen",
        aufgaben: ohne,
      });
    }
    return spalten;
  }, [zustaende.data, angereichert]);

  const rolleFuerProjekt = (projektId: string | undefined) =>
    (ich.data?.projekte.find((p) => p.projektId === projektId)?.rolle ??
      null) as Rolle | null;

  const rolleEffektive: Rolle | null | undefined =
    rolle ??
    rolleFuerProjekt(kontextProjektId ?? filter.projektIds[0]) ??
    (ich.data?.projekte
      .map((p) => p.rolle as Rolle)
      .sort((a, b) => {
        const rang = { gast: 0, mitglied: 1, betreuer: 2, verwalter: 3 };
        return (rang[b] ?? 0) - (rang[a] ?? 0);
      })[0] ?? null);

  const kannAnlegen = darfAnlegen(rolleEffektive, {
    rechte: ich.data?.rechte,
  });
  const kannArchiv = darfAktion({
    aktion: "aufgabe_archivieren",
    rolle: rolleEffektive,
    rechte: ich.data?.rechte,
  });

  function sucheFuerAufgaben(
    next: AufgabenFilter,
    naechsteAnsicht: "board" | "liste" | "zeit" = ansicht,
  ): Record<string, string> {
    const naechsteSuche = sucheAusFilter(next);
    if (festesProjektId) delete naechsteSuche["projekt"];
    if (search["reiter"]) naechsteSuche["reiter"] = search["reiter"];
    if (naechsteAnsicht !== "liste") {
      naechsteSuche["ansicht"] = naechsteAnsicht;
    }
    return naechsteSuche;
  }

  function setzeFilter(teil: Partial<AufgabenFilter>) {
    const next = { ...filter, ...teil };
    if (kontextProjektId) next.projektIds = [kontextProjektId];
    void navigate({
      to: ".",
      search: sucheFuerAufgaben(next),
      replace: true,
    });
  }

  function wechsleAnsicht(naechste: "board" | "liste" | "zeit") {
    setzeAnsicht(naechste);
    const next = { ...filter };
    void navigate({
      to: ".",
      search: sucheFuerAufgaben(next, naechste),
      replace: true,
    });
  }

  async function speichernNeu() {
    setzeFormFehler(null);
    const pid = kontextProjektId || projektNeu;
    if (!pid || !formTitel.trim()) return;
    try {
      const a = await anlegen.mutateAsync({
        projektId: pid,
        titel: formTitel.trim(),
      });
      setzeNeu(false);
      setzeFormTitel("");
      void navigate({ to: "/aufgaben/$id", params: { id: a.id } });
    } catch (e) {
      setzeFormFehler(fehlerMeldung(e));
    }
  }

  const art = leerArt({
    gesamtOhneFilter: aufgabenQ.data?.length ?? 0,
    gefiltert: angereichert.length,
    hatAktivenFilter: hatAktivenFilter(filter),
  });

  /* Zeitstrahl: Balken je Aufgabe aus Fälligkeit und Stand. */
  const fenster = useMemo(() => wochenFenster(jetzt), [jetzt]);
  const zeitZeilen = useMemo(
    () =>
      angereichert.map((a) => {
        const faellig = a.faelligAm ? new Date(a.faelligAm) : null;
        const zustandsGruppe = a.zustandId
          ? zustandsGruppen.get(a.zustandId)
          : undefined;
        const erledigt =
          zustandsGruppe === "erledigt" || zustandsGruppe === "verworfen";
        if (!faellig) {
          return { ...a, links: null, breite: null, fuellung: null, spanne: "" };
        }
        // Die Spanne beginnt zehn Tage vor der Frist — ohne Schätzung die
        // einzige ehrliche Näherung an den Arbeitszeitraum.
        const start = new Date(faellig);
        start.setDate(start.getDate() - 10);
        const links = positionInFenster(start, fenster.start, fenster.tage);
        const breite = Math.max(
          positionInFenster(faellig, fenster.start, fenster.tage) - links,
          1.5,
        );
        const fuellung = erledigt
          ? 100
          : Math.min(
              Math.max(
                ((jetzt.getTime() - start.getTime()) /
                  (faellig.getTime() - start.getTime())) *
                  100,
                0,
              ),
              100,
            );
        const spanne = `${start.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}–${faellig.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit" })}`;
        return { ...a, links, breite, fuellung, spanne };
      }),
    [angereichert, fenster, jetzt, zustandsGruppen],
  );

  const wochen = useMemo(() => {
    const liste: { label: string }[] = [];
    for (let i = 0; i < 8; i += 1) {
      const w = new Date(fenster.start);
      w.setDate(w.getDate() + i * 7);
      liste.push({
        label: `KW ${String(
          Math.ceil(
            ((w.getTime() - new Date(w.getFullYear(), 0, 1).getTime()) /
              86_400_000 +
              1) /
              7,
          ),
        ).padStart(2, "0")}`,
      });
    }
    return liste;
  }, [fenster]);

  const heutePosition = positionInFenster(jetzt, fenster.start, fenster.tage);

  const anzahlFilter =
    (filter.projektIds.length > 0 ? 1 : 0) +
    (filter.zustandIds.length > 0 ? 1 : 0) +
    (filter.erledigteAnzeigen ? 1 : 0) +
    (filter.wer.length > 0 ? 1 : 0) +
    (filter.meilensteinIds.length > 0 ? 1 : 0) +
    (filter.labelIds.length > 0 ? 1 : 0) +
    (filter.frist ? 1 : 0) +
    (filter.q ? 1 : 0);

  const filterzeile = (
    <div
      className="flex flex-wrap items-center gap-x-[22px] gap-y-1 py-[14px] font-mono text-[12px] text-tinte4"
      data-testid="filterzeile"
    >
      <span className="text-tinte">
        {kontextProjektId
          ? `Projekt ${projekteById.get(kontextProjektId)?.kuerzel ?? ""}`.trim()
          : filter.projektIds[0]
            ? `Projekt ${projekteById.get(filter.projektIds[0])?.kuerzel ?? ""}`.trim()
            : "Alle Projekte"}
      </span>
      <span className="text-tinte">
        Zustand:{" "}
        {filter.zustandIds.length === 0
          ? filter.erledigteAnzeigen
            ? "alle"
            : "aktive"
          : `${filter.zustandIds.length} gewählt`}
      </span>
      <span>
        Erledigte: {filter.erledigteAnzeigen ? "eingeblendet" : "ausgeblendet"}
      </span>
      <span>
        Frist:{" "}
        {filter.frist === "ueberfaellig"
          ? "überfällig"
          : filter.frist === "heute"
            ? "heute"
            : filter.frist === "woche"
              ? "diese Woche"
              : filter.frist === "ohne"
                ? "ohne Datum"
                : "alle"}
      </span>
      <span>Zuständig: {filter.wer.length === 0 ? "alle" : `${filter.wer.length} gewählt`}</span>
      {filter.q ? <span>Suche: „{filter.q}"</span> : null}
      <div className="relative">
        <button
          type="button"
          onClick={() => setzeFilterOffen((offen) => !offen)}
          aria-expanded={filterOffen}
          className="cursor-pointer font-mono text-[12px] text-akzent underline decoration-1 underline-offset-[3px]"
        >
          Filter
        </button>
        {filterOffen ? (
          <div className="absolute left-0 top-full z-30 mt-2 w-[280px] space-y-3 border border-linie bg-papier p-4 shadow-lg">
            {zeigeProjektFilter && !kontextProjektId ? (
              <div>
                <Label>Projekt</Label>
                <Select
                  value={filter.projektIds[0] ?? "alle"}
                  onValueChange={(v) =>
                    setzeFilter({
                      projektIds: v === "alle" ? [] : [v],
                    })
                  }
                >
                  <SelectTrigger className="mt-1 h-8 w-full">
                    <SelectValue placeholder="Projekt" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="alle">Alle Projekte</SelectItem>
                    {(projekte.data ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.kuerzel} — {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div className="flex items-center gap-2">
              <Schalter
                an={filter.erledigteAnzeigen}
                ariaLabel="Erledigte Aufgaben anzeigen"
                onKlick={() =>
                  setzeFilter({
                    erledigteAnzeigen: !filter.erledigteAnzeigen,
                  })
                }
              />
              <Label>Erledigte anzeigen</Label>
            </div>
            <div>
              <Label>Frist</Label>
              <Select
                value={filter.frist ?? "alle"}
                onValueChange={(v) =>
                  setzeFilter({
                    frist:
                      v === "alle"
                        ? undefined
                        : (v as AufgabenFilter["frist"]),
                  })
                }
              >
                <SelectTrigger className="mt-1 h-8 w-full">
                  <SelectValue placeholder="Frist" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="alle">Alle Fristen</SelectItem>
                  <SelectItem value="ueberfaellig">Überfällig</SelectItem>
                  <SelectItem value="heute">Heute</SelectItem>
                  <SelectItem value="woche">Diese Woche</SelectItem>
                  <SelectItem value="ohne">Ohne Datum</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="aufgaben-suche">Suche</Label>
              <Input
                id="aufgaben-suche"
                className="mt-1 h-8"
                placeholder="Text …"
                value={filter.q ?? ""}
                onChange={(e) => setzeFilter({ q: e.target.value || undefined })}
              />
            </div>
            {hatAktivenFilter(filter) ? (
              <Button
                variant="ghost"
                className="h-8 w-full"
                onClick={() => setzeFilter(leererFilter())}
              >
                Filter zurücksetzen
              </Button>
            ) : null}
          </div>
        ) : null}
      </div>
    </div>
  );

  if (aufgabenQ.isLoading || projekte.isLoading || zustaende.isLoading) {
    return <Mikrolabel grad={12}>Lade Aufgaben …</Mikrolabel>;
  }

  return (
    <div className={randlos ? "pt-8 lg:pt-14" : undefined}>
      {mitKopf ? (
        <div className={seitenrand}>
          <Seitenkopf
            kontext={`${projektIds.length} ${projektIds.length === 1 ? "Projekt" : "Projekte"} sichtbar`}
          >
            Aufgaben
          </Seitenkopf>
          <Reiterleiste className="mt-[34px]">
            <Reiter
              aktiv={ansicht === "board"}
              onKlick={() => wechsleAnsicht("board")}
              testId="ansicht-board"
            >
              Board
            </Reiter>
            <Reiter
              aktiv={ansicht === "liste"}
              onKlick={() => wechsleAnsicht("liste")}
              testId="ansicht-liste"
            >
              Liste
            </Reiter>
            <Reiter
              aktiv={ansicht === "zeit"}
              onKlick={() => wechsleAnsicht("zeit")}
              testId="ansicht-zeit"
            >
              Zeitstrahl
            </Reiter>
            <Mikrolabel grad={11.5} className="ml-auto flex-none pb-1.5">
              {angereichert.length} Aufgaben · {anzahlFilter} Filter
            </Mikrolabel>
          </Reiterleiste>
          {filterzeile}
        </div>
      ) : null}

      {art ? (
        <div className={cx("pt-4", seitenrand)}>
          <Leerzustand
            titel={
              leerText(
                art,
                kontextProjektId ? "projekt_aufgaben" : "aufgaben",
              ).titel
            }
            hinweis={
              leerText(
                art,
                kontextProjektId ? "projekt_aufgaben" : "aufgaben",
              ).hinweis
            }
            onAktion={
              art === "nichts" && kannAnlegen
                ? () => setzeNeu(true)
                : art === "filter_leer"
                  ? () => setzeFilter(leererFilter())
                  : undefined
            }
            aktionLabel={
              art === "nichts" && kannAnlegen
                ? "Aufgabe anlegen"
                : art === "filter_leer"
                  ? "Filter löschen"
                  : undefined
            }
          />
        </div>
      ) : ansicht === "board" ? (
        <div
          className="overflow-x-auto"
          data-testid="aufgaben-board"
        >
          <div className="flex min-h-[620px] min-w-full items-stretch">
            {boardSpalten.map((spalte) => (
              <section
                key={spalte.id}
                className="min-w-[224px] flex-1 basis-0"
                aria-label={spalte.name}
              >
                {/* Der Spaltenkopf sitzt auf der Tafel — das einzige dunkle
                    Band der Seite, wie der Entwurf es zeichnet. */}
                <header className="flex h-[46px] items-center gap-2.5 bg-tafel px-5 text-tafel-tinte">
                  <span
                    aria-hidden="true"
                    className={cx(
                      "block size-[7px] flex-none",
                      TAFEL_PUNKT[spalte.gruppe] ?? "bg-tafel-tinte3",
                    )}
                  />
                  <Mikrolabel grad={12} ton="tafel" className="text-tafel-tinte">
                    {spalte.name}
                  </Mikrolabel>
                  <Mikrolabel
                    grad={11}
                    zahl
                    className="ml-auto text-tafel-tinte3"
                  >
                    {spalte.aufgaben.length}
                  </Mikrolabel>
                </header>
                <div className="min-h-[574px] border-l border-linie">
                  {spalte.aufgaben.map((a) => {
                    const proj = projekteById.get(a.projektId);
                    const kennung = proj
                      ? `${proj.kuerzel}-${a.nummer}`
                      : String(a.nummer);
                    const fristgruppe = a.gruppe ?? "ohne_datum";
                    const frist = formuliereFrist(
                      { faelligAm: a.faelligAm, erledigt: istAbgeschlossen(fristgruppe) },
                      fristgruppe,
                      zeitzone,
                      jetzt,
                    );
                    return (
                      <div
                        key={a.id}
                        className="cursor-pointer border-b border-linie px-5 pb-[18px] pt-4 transition-colors duration-150 ease-aus hover:bg-wisch"
                        data-testid="board-karte"
                      >
                        <div className="flex items-center gap-2.5">
                          <Mikrolabel grad={11}>{kennung}</Mikrolabel>
                          <Prioritaetspunkte
                            stufe={a.prioritaet ?? 0}
                            className="ml-auto"
                          />
                        </div>
                        <Link
                          to="/aufgaben/$id"
                          params={{ id: a.id }}
                          className="mt-2.5 block text-pretty text-[14px] font-normal leading-[1.4] text-tinte hover:underline"
                        >
                          {a.titel}
                        </Link>
                        <div className="mt-3.5 flex items-center gap-2.5">
                          <Mikrolabel
                            grad={11}
                            ariaLabel={`Aufgabenart: ${aufgabenartLabel(a.art)}`}
                          >
                            {aufgabenartLabel(a.art)}
                          </Mikrolabel>
                          {a.inhalt ? (
                            <Mikrolabel
                              grad={10.5}
                              className="truncate"
                              testId="aufgaben-beschreibung"
                            >
                              {markdownAuszug(a.inhalt)}
                            </Mikrolabel>
                          ) : null}
                          {frist ? (
                            <Mikrolabel
                              grad={11}
                              zahl
                              betont={fristgruppe === "ueberfaellig"}
                              className={cx(
                                fristgruppe === "ueberfaellig" &&
                                  "font-medium text-akzent",
                              )}
                            >
                              {frist}
                            </Mikrolabel>
                          ) : null}
                          <Mikrolabel grad={10} className="ml-auto">
                            {proj?.kuerzel ?? ""}
                          </Mikrolabel>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </section>
            ))}
          </div>
        </div>
      ) : ansicht === "zeit" ? (
        <div className="border-t border-tinte" data-testid="aufgaben-zeitstrahl">
          <div className="flex items-stretch">
            {/* Linke Spalte: Gruppen mit Frist, darunter die Zeilen. */}
            <div className="w-[268px] flex-none border-r border-linie">
              <div className="h-[34px]" />
              {AUFGABEN_GRUPPEN.map((g) => {
                const zeilen = zeitZeilen.filter((z) => (z.gruppe ?? "ohne_datum") === g);
                if (zeilen.length === 0) return null;
                return (
                  <div key={g}>
                    <div className="flex h-8 items-center px-9">
                      <Mikrolabel grad={11} betont className={GRUPPEN_TON[g]}>
                        {GRUPPEN_LABEL[g]}
                      </Mikrolabel>
                      <Mikrolabel grad={10.5} className="ml-auto">
                        {g === "ueberfaellig" ? "überfällig" : g === "heute" ? "heute" : g === "diese_woche" ? "diese Woche" : g === "spaeter" ? "später" : ""}
                      </Mikrolabel>
                    </div>
                    {zeilen.map((z) => {
                      const proj = projekteById.get(z.projektId);
                      return (
                        <Link
                          key={z.id}
                          to="/aufgaben/$id"
                          params={{ id: z.id }}
                          className="flex h-[38px] cursor-pointer items-center gap-2.5 px-9 transition-colors duration-150 ease-aus hover:bg-wisch"
                        >
                          <Mikrolabel grad={10.5} className="flex-none">
                            {proj ? `${proj.kuerzel}-${z.nummer}` : z.nummer}
                          </Mikrolabel>
                          <span className="min-w-0 flex-1 truncate text-[12.5px] font-light text-tinte">
                            {z.titel}
                          </span>
                        </Link>
                      );
                    })}
                  </div>
                );
              })}
            </div>

            {/* Rechte Seite: Wochenachse, Heute-Linie, Balken. */}
            <div className="relative min-w-0 flex-1">
              <div className="flex h-[34px] border-b border-linie">
                {wochen.map((w) => (
                  <span
                    key={w.label}
                    className="flex flex-1 items-center border-l border-linie pl-[9px] font-mono text-[10.5px] text-tinte4"
                  >
                    {w.label}
                  </span>
                ))}
              </div>
              <span
                aria-hidden="true"
                className="absolute bottom-0 top-[34px] z-[2] w-px bg-akzent"
                style={{ left: `${heutePosition}%` }}
              />
              <span
                className="absolute top-[41px] z-[3] -translate-x-1/2 bg-papier px-[5px] font-mono text-[10px] text-akzent"
                style={{ left: `${heutePosition}%` }}
              >
                heute
              </span>
              {AUFGABEN_GRUPPEN.map((g) => {
                const zeilen = zeitZeilen.filter((z) => (z.gruppe ?? "ohne_datum") === g);
                if (zeilen.length === 0) return null;
                return (
                  <div key={g}>
                    <div className="h-8" />
                    {zeilen.map((z) => (
                      <div
                        key={z.id}
                        className="flex h-[38px] items-center pr-5"
                      >
                        {z.links !== null && z.breite !== null ? (
                          <>
                            <span
                              className="relative h-3 flex-1 shadow-[inset_0_0_0_1px_var(--linie3)]"
                              style={{ background: "var(--wisch)" }}
                            >
                              <span
                                className="absolute bottom-0 top-0"
                                style={{
                                  left: `${z.links}%`,
                                  width: `${z.breite}%`,
                                  background: "var(--wisch)",
                                  boxShadow: "inset 0 0 0 1px var(--linie3)",
                                }}
                              />
                              <span
                                className="absolute bottom-0 top-0"
                                style={{
                                  left: `${z.links}%`,
                                  width: `${(z.breite * (z.fuellung ?? 0)) / 100}%`,
                                  background:
                                    z.gruppe === "ueberfaellig"
                                      ? "var(--akzent)"
                                      : "var(--tinte)",
                                }}
                              />
                            </span>
                            <span className="w-[78px] flex-none pl-3 font-mono text-[10.5px] tabular-nums text-tinte4">
                              {z.spanne}
                            </span>
                          </>
                        ) : (
                          <span className="flex-1" />
                        )}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="mx-9 mt-[26px] flex flex-wrap items-center gap-[26px] border-t border-linie pt-[18px] font-mono text-[11px] text-tinte4">
            <span className="flex items-center gap-2">
              <span className="h-2 w-[22px] bg-wisch shadow-[inset_0_0_0_1px_var(--linie3)]" />
              geplante Spanne
            </span>
            <span className="flex items-center gap-2">
              <span className="h-2 w-[22px] bg-tinte" />
              Stand
            </span>
            <span className="flex items-center gap-2">
              <span className="h-2 w-[22px] bg-akzent" />
              überfällig
            </span>
            <span className="ml-auto">
              Kalenderwochen · Fristen, nicht Kapazität
            </span>
          </div>
        </div>
      ) : (
        /*
          Als eigene Seite: eigene 36px, sonst unverändert.

          Ohne Trennlinie oben. Der Entwurf setzt dort eine, aber bei ihm ist
          die Filterzeile darüber randlos; hier schließt sie bereits mit einer
          Linie ab. Zwei Striche in wenigen Pixeln Abstand liest man als
          Fehler, nicht als Gliederung.
        */
        <div
          className={cx(
            "space-y-9",
            randlos && "pb-24 pt-[34px]",
            seitenrand,
          )}
          data-testid="aufgaben-liste"
        >
          {AUFGABEN_GRUPPEN.map((g) => {
            const zeilen = gruppiert.get(g) ?? [];
            if (zeilen.length === 0) return null;
            return (
              <section key={g}>
                <Rubrik
                  anzahl={zeilen.length}
                  aktion={
                    <Mikrolabel grad={11} className="flex-none">
                      {zeilen.reduce(
                        (summe, z) => summe + (z.prioritaet ?? 0),
                        0,
                      ) > 0
                        ? ""
                        : ""}
                    </Mikrolabel>
                  }
                >
                  <span className={GRUPPEN_TON[g]}>{GRUPPEN_LABEL[g]}</span>
                </Rubrik>
                <ul role="list">
                  {zeilen.map((a) => {
                    const proj = projekteById.get(a.projektId);
                    const zustand = a.zustandId
                      ? zustaendeById.get(a.zustandId)
                      : undefined;
                    const gruppe = (a.gruppe ?? "ohne_datum") as AufgabenGruppe;
                    const frist = formuliereFrist(
                      {
                        faelligAm: a.faelligAm,
                        erledigt: istAbgeschlossen(gruppe),
                      },
                      gruppe,
                      zeitzone,
                      jetzt,
                    );
                    const kennung = proj
                      ? `${proj.kuerzel}-${a.nummer}`
                      : String(a.nummer);
                    return (
                      <li key={a.id}>
                        <Link
                          to="/aufgaben/$id"
                          params={{ id: a.id }}
                          className={zeileKlassen({
                            hoehe: "auto",
                            klickbar: true,
                          })}
                          data-testid="aufgaben-zeile"
                        >
                          <span className="flex flex-none flex-col gap-0.5">
                            <Prioritaetspunkte stufe={a.prioritaet ?? 0} />
                          </span>
                          <Kennung>{kennung}</Kennung>
                          <Zeilentitel titel={a.titel}>{a.titel}</Zeilentitel>
                          {zustand ? (
                            <span className="flex w-24 flex-none items-center gap-2">
                              <Zustandspunkt
                                bild={
                                  ZUSTAND[zustandAusGruppe(zustand.gruppe)]
                                }
                              />
                              <Mikrolabel grad={11} ton="mittel">
                                {zustand.name}
                              </Mikrolabel>
                            </span>
                          ) : null}
                          <Mikrolabel grad={10} className="w-6 flex-none">
                            {proj?.kuerzel ?? ""}
                          </Mikrolabel>
                          {frist ? (
                            <Mikrolabel
                              grad={11.5}
                              zahl
                              betont={gruppe === "ueberfaellig"}
                              className={cx(
                                "w-[58px] flex-none text-right",
                                gruppe === "ueberfaellig" &&
                                  "font-medium text-akzent",
                              )}
                            >
                              {frist}
                            </Mikrolabel>
                          ) : (
                            <span className="w-[58px] flex-none" />
                          )}
                          {kannArchiv ? (
                            <Button
                              variant="ghost"
                              className="h-7 px-2 opacity-0 transition-opacity group-hover:opacity-100 group-focus-within:opacity-100"
                              onClick={(e) => {
                                e.preventDefault();
                                void archivMut.mutateAsync({
                                  id: a.id,
                                  projektId: a.projektId,
                                  meilensteinId: a.meilensteinId,
                                });
                              }}
                            >
                              Archiv
                            </Button>
                          ) : null}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </section>
            );
          })}
          {angereichert.length > 0 ? (
            <div className="flex items-center gap-4">
              <span className={verweisKlassen({ grad: 12 })}>
                {angereichert.length} von {aufgabenQ.data?.length ?? angereichert.length} sichtbar
              </span>
            </div>
          ) : null}
        </div>
      )}

      <Dialog open={neuOffen} onOpenChange={setzeNeu}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Neue Aufgabe</DialogTitle>
          </DialogHeader>
          <div className="mt-4 space-y-3">
            {!kontextProjektId ? (
              <div>
                <Label>Projekt</Label>
                <Select value={projektNeu} onValueChange={setzeProjektNeu}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Projekt wählen" />
                  </SelectTrigger>
                  <SelectContent>
                    {(projekte.data ?? []).map((p) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.kuerzel} — {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null}
            <div>
              <Label htmlFor="titel">Titel</Label>
              <Input
                id="titel"
                className="mt-1"
                value={formTitel}
                onChange={(e) => setzeFormTitel(e.target.value)}
              />
            </div>
            {formFehler ? (
              <Fehlerzeile
                meldung={formFehler}
                onNeuLaden={() => void aufgabenQ.refetch()}
              />
            ) : null}
          </div>
          <DialogFooter className="mt-4">
            <Button variant="secondary" onClick={() => setzeNeu(false)}>
              Abbrechen
            </Button>
            <Button
              onClick={() => void speichernNeu()}
              disabled={
                !formTitel.trim() ||
                !(kontextProjektId || projektNeu) ||
                anlegen.isPending
              }
            >
              Anlegen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
